// src/services/about.js — the About status, in the browser.
//
// 🟢 THE SAME THREE FIELDS THE PHONE WRITES, on `users/{uid}`:
//      aboutStatus           string   — "🟢 Free to chat" (emoji and text in one)
//      aboutStatusExpiresAt  number|null — absolute epoch ms; null = never runs out
//      aboutRecents          string[] — the last few the person typed
//    and the visibility pair inside `privacySettings`:
//      aboutVisibility        'everyone' | 'my_contacts' | 'nobody' | 'except'
//      aboutVisibilityExcept  string[] of uids
//    So a status set on a phone is readable here, and a visibility changed here
//    is honoured there — the sync is Firestore, not a message anyone delivers.
//
// 🟥 THIS RULE IS THE WHOLE OF THE PROTECTION. `firestore.rules` lets any
//    signed-in account read any user document, `aboutStatus` and
//    `privacySettings` included — there is no server-side enforcement of who may
//    see a status. It is a deliberate port of services/aboutStatus.ts on the
//    phone, case for case, because the two clients disagreeing here does not
//    look like a bug: it looks like the app leaking something the person hid.
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

export const ABOUT_PRESETS = [
  { emoji: '🟢', text: 'Free to chat' },
  { emoji: '⏰', text: 'Slow to respond' },
  { emoji: '🤣', text: 'Hanging with friends' },
  { emoji: '✈️', text: 'Traveling' },
  { emoji: '🔥', text: 'Feeling excited!' },
];

/* 🟥 THE SAME FIVE DURATIONS THE PHONE OFFERS, with the same keys and the same
   milliseconds — a case-for-case port of ABOUT_DURATIONS in
   allo-app/services/aboutStatus.ts. A browser offering "12 hours" would write
   an expiry the phone's own screen could not describe, and the phone re-opens
   its editor on the saved key: an unrecognised one silently becomes "never
   expires". */
export const ABOUT_DURATIONS = [
  { key: '1h', label: '1 hour', ms: 60 * 60 * 1000 },
  { key: '8h', label: '8 hours', ms: 8 * 60 * 60 * 1000 },
  { key: '1d', label: '1 day', ms: 24 * 60 * 60 * 1000 },
  { key: '2d', label: '2 days', ms: 2 * 24 * 60 * 60 * 1000 },
  { key: '1w', label: '1 week', ms: 7 * 24 * 60 * 60 * 1000 },
];

/** How many previously-typed statuses the editor offers back. */
export const MAX_RECENTS = 3;

/* 🟢 THE PHONE'S EMOJI SHORTCUTS, so a status composed here can carry the same
   leading emoji without hunting through an OS picker. Mirrors the list in
   allo-app/screens/AboutScreen.tsx; unlike the durations above, a difference
   here costs nothing but a different set of shortcuts — the emoji is stored as
   part of the string either way. */
export const ABOUT_EMOJIS = [
  '😀', '😂', '🥹', '😍', '😎', '🤔', '😴', '🥳', '😭', '😡',
  '👍', '🙏', '👏', '💪', '🔥', '✨', '⭐', '❤️', '🎉', '☕',
  '✈️', '🏖️', '💼', '📚', '⚽', '🎮', '🎵', '💻', '💤', '🌙',
];

export const ABOUT_VISIBILITY = [
  { id: 'everyone', label: 'Everyone' },
  { id: 'my_contacts', label: 'My contacts' },
  { id: 'except', label: 'My contacts except…' },
  { id: 'nobody', label: 'Nobody' },
];

/** A status with no expiry never runs out — that is what `null` has always meant. */
export function isAboutExpired(expiresAt) {
  if (!expiresAt) return false;
  return Date.now() > expiresAt;
}

/** The live status on a user document, or null when there is nothing to show. */
export function aboutFromDoc(data) {
  const text = String(data?.aboutStatus || '');
  if (!text.trim()) return null;
  const expiresAt = data?.aboutStatusExpiresAt ?? null;
  if (isAboutExpired(expiresAt)) return null;
  return { text, expiresAt };
}

export function needsMutualCheck(data) {
  return (data?.privacySettings?.aboutVisibility || 'everyone') === 'my_contacts';
}

/**
 * May `viewerUid` see this owner's status?
 *
 * 🟢 'my_contacts' IS MUTUAL — each side must have the other. `mutual` is passed
 *    in rather than fetched so this stays pure and the caller decides whether
 *    the lookup is worth doing.
 * 🟥 An unrecognised setting reads as 'everyone', matching the phone. Failing
 *    closed on a typo would silently hide the status of everybody who has never
 *    opened the privacy screen.
 */
export function canSeeAbout({ viewerUid, ownerData, mutual }) {
  if (!viewerUid || !ownerData) return false;
  const vis = ownerData?.privacySettings?.aboutVisibility || 'everyone';
  if (vis === 'nobody') return false;
  if (vis === 'except') {
    const excluded = ownerData?.privacySettings?.aboutVisibilityExcept || [];
    return !excluded.includes(viewerUid);
  }
  if (vis === 'my_contacts') return !!mutual;
  return true;
}

/* one read of each side's contacts, briefly cached — a chat list must not
   re-read them per render */
const _mutualCache = new Map();
const TTL = 60000;

async function hasContact(ownerUid, peerUid) {
  const now = Date.now();
  let entry = _mutualCache.get(ownerUid);
  if (!entry || now - entry.at > TTL) {
    try {
      const snap = await getDoc(doc(db, 'userConnections', ownerUid));
      const conns = snap.exists() ? (snap.data().connections || []) : [];
      const has = new Set();
      /* entries key the peer as `.uid` on some writes and `.id` on others */
      conns.forEach((c) => { if (c?.uid) has.add(c.uid); if (c?.id) has.add(c.id); });
      entry = { has, at: now };
    } catch {
      entry = { has: new Set(), at: now, failed: true };
    }
    _mutualCache.set(ownerUid, entry);
  }
  return entry.has.has(peerUid);
}

/** Do the two people have each other? Both directions, because the rule is mutual. */
export async function areMutualContacts(a, b) {
  /* 🟥 A FAILED LOOKUP IS NOT A YES — hasContact returns false on error, so an
     unreadable contacts document hides the status rather than revealing it to
     someone who chose contacts-only precisely to avoid that. */
  const [aHasB, bHasA] = await Promise.all([hasContact(a, b), hasContact(b, a)]);
  return aHasB && bHasA;
}

/** The whole question: read the owner, apply expiry, apply privacy, answer. */
export async function loadAboutFor(viewerUid, ownerUid) {
  if (!viewerUid || !ownerUid || viewerUid === ownerUid) return null;
  try {
    const snap = await getDoc(doc(db, 'users', ownerUid));
    if (!snap.exists()) return null;
    const data = snap.data();
    const about = aboutFromDoc(data);
    if (!about) return null;
    const mutual = needsMutualCheck(data) ? await areMutualContacts(ownerUid, viewerUid) : false;
    return canSeeAbout({ viewerUid, ownerData: data, mutual }) ? about : null;
  } catch {
    return null;
  }
}

/* ── writing a status ──────────────────────────────────────────────────────
   🟥 THE BROWSER COULD ONLY READ ONE. Every rule above — expiry, privacy, the
      mutual-contacts test — existed here so the web could DISPLAY a status the
      phone had set, and there was no way to set one from a keyboard. The fields
      below are the phone's own, built by the same three functions its About
      screen now calls, so a status typed here is indistinguishable from one
      typed there and either can end it. ─────────────────────────────────── */

/**
 * The list the editor offers back, newest first.
 *
 * 🟢 IT KEEPS WHAT WAS TYPED, NOT WHAT WAS TAPPED. A status identical to one of
 *    the presets is already on screen a row above; repeating it there would
 *    spend one of three slots saying something the user can already see.
 */
export function pushRecentStatus(list, status) {
  const clean = String(status || '').trim();
  const prev = (Array.isArray(list) ? list : []).map((s) => String(s || '').trim()).filter(Boolean);
  if (!clean) return prev.slice(0, MAX_RECENTS);
  const isPreset = ABOUT_PRESETS.some((p) => `${p.emoji} ${p.text}` === clean || p.text === clean);
  if (isPreset) return prev.slice(0, MAX_RECENTS);
  /* newest first, and a repeat MOVES rather than duplicates */
  return [clean, ...prev.filter((s) => s !== clean)].slice(0, MAX_RECENTS);
}

/* one emoji: a pictographic base, any ZWJ-joined parts, a variation selector
   and a skin-tone modifier — the phone's own pattern, from AboutScreen.tsx */
const EMOJI_HEAD = /^(\p{Extended_Pictographic}(?:\u200D\p{Extended_Pictographic})*(?:\uFE0F)?(?:\p{Emoji_Modifier})?)/u;

/**
 * A stored status split back into its leading emoji and its words.
 *
 * 🟥 THE EMOJI AND THE TEXT ARE ONE STRING ON THE DOCUMENT — "🟢 Free to chat"
 *    — because that is what the phone has always written and what every reader
 *    of `aboutStatus` displays. Splitting it is a thing the EDITOR does so it
 *    can swap the emoji without eating the first word, which is exactly what a
 *    naive "strip the first word" does to "Free to chat".
 */
export function splitStatus(str) {
  const s = String(str || '');
  const m = s.match(EMOJI_HEAD);
  if (!m) return { emoji: '', text: s };
  return { emoji: m[1], text: s.slice(m[1].length).trimStart() };
}

/** The status with `emoji` at the front, replacing one already there. */
export function withEmoji(str, emoji) {
  const { text } = splitStatus(str);
  if (!emoji) return text;
  return text ? `${emoji} ${text}` : emoji;
}

/** How long a duration key lasts, or null for a key nothing recognises. */
export function durationMsOf(key) {
  const d = ABOUT_DURATIONS.find((x) => x.key === key);
  return d ? d.ms : null;
}

/**
 * The fields a client writes when somebody sets a status.
 *
 * 🟥 ONE BUILDER, TWO CLIENTS. The four fields have to agree exactly or a status
 *    set in the browser expires when the phone does not expect it to — or worse,
 *    never, because one side wrote `undefined` where the other writes `null` and
 *    `aboutFromDoc` reads a missing expiry as "runs forever".
 *
 * 🟢 AN EMPTY STATUS NEVER CARRIES AN EXPIRY. Saving an empty field is one of
 *    the ways to end a status, and leaving a timestamp on it would mean a
 *    cleared status that "expires" later — a state nothing reads.
 */
export function buildAboutWrite({ status, durationKey, recents, now }) {
  const at = now || Date.now();
  const clean = String(status || '').trim();
  const ms = durationMsOf(durationKey);
  return {
    aboutStatus: clean,
    aboutStatusExpiresAt: clean && ms ? at + ms : null,
    aboutStatusUpdatedAt: at,
    aboutRecents: pushRecentStatus(recents, clean),
  };
}

/**
 * The fields a client writes when somebody ends a status.
 *
 * 🟥 THE RECENTS LIST IS NOT TOUCHED. Clearing a status says "I am not this
 *    right now", not "I never want to say this again".
 */
export function clearAboutWrite(now) {
  return {
    aboutStatus: '',
    aboutStatusExpiresAt: null,
    aboutStatusUpdatedAt: now || Date.now(),
  };
}

/**
 * "Expires in about 3 hours" / "Doesn't expire", for the editor's own line.
 *
 * 🟢 WEB ONLY, AND DELIBERATELY SO: the phone shows the duration you picked,
 *    because you are picking it. The browser shows what is on the document,
 *    which may have been set on the phone hours ago — a remembered key would be
 *    a guess about someone else's choice.
 */
export function expiryLabel(expiresAt, now) {
  if (!expiresAt) return 'Doesn’t expire';
  const left = expiresAt - (now || Date.now());
  if (left <= 0) return 'Expired';
  const mins = Math.round(left / 60000);
  if (mins < 60) return `Expires in ${mins} minute${mins === 1 ? '' : 's'}`;
  const hours = Math.round(left / 3600000);
  if (hours < 48) return `Expires in ${hours} hour${hours === 1 ? '' : 's'}`;
  const days = Math.round(left / 86400000);
  return `Expires in ${days} day${days === 1 ? '' : 's'}`;
}

/**
 * The reply the app builds when somebody taps a status bubble.
 *
 * 🟥 THE SHAPE IS NOT NEGOTIABLE. The phone renders `replyTo.type === 'about'`
 *    as the floating white bubble rather than a normal quote, and it refuses to
 *    scroll to `messageId` because no such message exists. A browser that sent a
 *    different shape would put a quote of a message nobody can open into the
 *    other person's chat.
 */
export function aboutReplyTarget(ownerUid, text) {
  return {
    id: `about:${ownerUid || ''}`,
    senderId: ownerUid || '',
    text: text || '',
    type: 'about',
  };
}
